import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputAdornment,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { ReactComponent as ExcelExportIcon } from "../assets/img/icons/excel_export.svg";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import QueryBuilderRoundedIcon from "@mui/icons-material/QueryBuilderRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const FRONTEND_URL =
  process.env.REACT_APP_FRONT_BASE_URL || "http://localhost:3001";

const ShowActiveLinks = () => {
  const selectedDM = JSON.parse(localStorage.getItem("user"));
  const contactDM = selectedDM.mobile;
  const selectedManufacturer = JSON.parse(localStorage.getItem("selectedMfr"));
  const mfr_id = selectedManufacturer?.id;
  const store_id = JSON.parse(localStorage.getItem("store_id"));

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeLinks, setActiveLinks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterDate, setFilterDate] = useState("all");
  const [itemsLockedCount, setItemsLockedCount] = useState({});
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [refreshPage, setRefreshPage] = useState(false);
  const [openDeleteDialogBox, setOpenDeleteDialogBox] = useState({
    open: false,
    linkName: null,
    linkID: null,
  });
  const ITEMS_PER_PAGE = 10;
  const [page, setPage] = useState(1);

  // Snackbar config
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setSnackbarMsg("");
  };

  // Get all active links
  useEffect(() => {
    if (!mfr_id) return;

    const getAllActiveLinks = async () => {
      try {
        const activeLinksFetcher = await axios.get(
          `${API_BASE_URL}/api/auth/links/${mfr_id}`,
        );

        if (activeLinksFetcher.data?.statusCode !== 200)
          throw activeLinksFetcher.data;

        const allActiveLinks = activeLinksFetcher.data?.data;
        setActiveLinks(allActiveLinks);
        fetchLockedCounts(allActiveLinks);
      } catch (error) {
        console.error(error);
      }
    };

    getAllActiveLinks();
  }, [refreshPage, mfr_id]);

  // Date & Time formatter
  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-start",
            gap: 0.5,
          }}
        >
          <CalendarMonthRoundedIcon sx={{ fill: "#42A996" }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            {day}-{month}-{year}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-start",
            gap: 0.5,
          }}
        >
          <QueryBuilderRoundedIcon sx={{ fill: "#42A996" }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            {hours}:{minutes}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Manual encryption
  const encodeType = (text) => {
    const salt = "XyZ123";
    const salted = `${text}::${salt}`;
    return btoa(salted)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  };

  // Fetch tiny url (is.gd version – shortest, free, unlimited)
  const getTinyUrl = async (longUrl) => {
    try {
      const apiURL = `https://is.gd/create.php?format=simple&url=${encodeURIComponent(
        longUrl,
      )}`;

      const res = await fetch(apiURL);
      const shortUrl = await res.text();

      // If is.gd returns a valid short link
      if (shortUrl && shortUrl.startsWith("https://")) {
        return shortUrl.trim();
      }

      return longUrl; // fallback
    } catch (err) {
      console.error("Tiny URL error:", err);
      return longUrl; // fallback
    }
  };

  // Building the link URL to use and attach in the excel sheet
  const buildLinkUrl = (link) => {
    const encryptedType = encodeType(link.type);
    const sanitizedLinkName = link.name.trim().replace(/\s+/g, "-");

    let isSet = false;
    try {
      const parsed = JSON.parse(link.filterCondition);
      if (parsed?.set === true) isSet = true;
    } catch (e) {
      console.error("Error parsing filterCondition", e);
    }

    let path = `${FRONTEND_URL}/getstockdata`;
    if (isSet) path += "/set";

    // return `${path}/${sanitizedLinkName}/${link.linkId}/${mfr_id}/${encryptedType}`;
    return `${path}/${sanitizedLinkName}/${store_id}/${link.linkId}/${mfr_id}/${encryptedType}/${contactDM}`;
  };

  // URL copy handler
  const handleCopy = async (linkName, linkId, linkType, filterCondition) => {
    const link = {
      name: linkName,
      linkId,
      type: linkType,
      filterCondition,
    };

    const fullLink = buildLinkUrl(link);

    try {
      // Strategy 1: Try modern clipboard API first (works on most devices)
      if (navigator.clipboard && window.isSecureContext) {
        try {
          // Copy full link immediately to maintain user gesture on iOS
          await navigator.clipboard.writeText(fullLink);

          // Show immediate feedback
          setCopiedLinkId(linkId);
          setTimeout(() => setCopiedLinkId(null), 2000);
          setOpenSnackbar(true);
          setSnackbarMsg("Link copied!");
          setSnackbarSeverity("success");

          // Silently get and update with tiny URL in background
          try {
            const tinyUrl = await getTinyUrl(fullLink);
            await navigator.clipboard.writeText(tinyUrl);
          } catch (tinyUrlError) {
            // Tiny URL failed, but full link already copied - no user impact
            console.warn("Tiny URL generation failed:", tinyUrlError);
          }

          return;
        } catch (clipboardError) {
          console.warn("Clipboard API failed, falling back:", clipboardError);
          // Fall through to fallback method
        }
      }

      // Strategy 2: Fallback for older browsers or when clipboard API fails
      const tinyUrl = await getTinyUrl(fullLink);
      fallbackCopy(tinyUrl, linkId);
    } catch (error) {
      // Last resort: try to copy full link with fallback
      console.error("Error in copy process:", error);
      fallbackCopy(fullLink, linkId);
    }
  };
  // Filter links
  const filteredLinks = useMemo(() => {
    return activeLinks.filter((link) => {
      const matchSearch = link.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchType =
        filterType === "all" ? true : link.type === filterType.toLowerCase();

      const createdDate = new Date(link.createdAt).toISOString().split("T")[0];
      const today = new Date().toISOString().split("T")[0];

      let matchDate = true;
      if (filterDate === "today") {
        matchDate = createdDate === today;
      } else if (filterDate === "week") {
        const now = new Date();
        const startOfWeek = new Date();
        startOfWeek.setDate(now.getDate() - 7);
        matchDate = new Date(link.createdAt) >= startOfWeek;
      } else if (filterDate === "month") {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        matchDate = new Date(link.createdAt) >= startOfMonth;
      }

      return matchSearch && matchType && matchDate;
    });
  }, [activeLinks, searchTerm, filterType, filterDate]);

  // For pagination
  const paginatedLinks = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredLinks.slice(startIndex, endIndex);
  }, [filteredLinks, page]);
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterType, filterDate]);

  // Get all locked items of a link
  const fetchLockedCounts = async (links) => {
    const result = {};
    await Promise.all(
      links.map(async (link) => {
        try {
          const res = await axios.get(
            `${API_BASE_URL}/api/auth/stock/active?linkid=${link.linkId}`,
          );
          result[link.linkId] = res.data?.count || 0;
        } catch (err) {
          result[link.linkId] = 0;
        }
      }),
    );
    setItemsLockedCount(result);
  };

  // Export table data to Excel
  const exportToExcel = () => {
    try {
      const XLSX = require("xlsx");

      const excelData = filteredLinks.map((link) => ({
        "Link Title": link.name,
        Type: link.type,
        "Total Views": link.views,
        "Items in Cart": itemsLockedCount[link.linkId] ?? "--",
        "Date Created": formatDate(link.createdAt),
        "Link URL": buildLinkUrl(link), // ✅ NEW COLUMN
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Active Links");

      XLSX.writeFile(
        workbook,
        `ActiveLinks_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );

      setOpenSnackbar(true);
      setSnackbarSeverity("success");
      setSnackbarMsg("Excel exported successfully!");
    } catch (error) {
      console.error(error);
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
      setSnackbarMsg("Failed to export Excel");
    }
  };

  // Fallback link URL copy handler
  // const fallbackCopy = (text) => {
  //   const textarea = document.createElement("textarea");
  //   textarea.value = text;
  //   textarea.style.position = "fixed";
  //   textarea.style.opacity = "0";
  //   document.body.appendChild(textarea);
  //   textarea.focus();
  //   textarea.select();

  //   try {
  //     document.execCommand("copy");
  //     console.log("Copied using fallback method");
  //   } catch (err) {
  //     console.error("Fallback copy failed", err);
  //   }

  //   document.body.removeChild(textarea);
  // };
  const fallbackCopy = (text, linkId) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;

    // 🔒 Prevent scroll jump and make invisible
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.left = "-9999px";
    textarea.style.opacity = "0";
    textarea.setAttribute("readonly", ""); // Prevent keyboard on mobile

    document.body.appendChild(textarea);

    // iOS compatibility
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, 99999); // For mobile devices

    try {
      const successful = document.execCommand("copy");

      if (successful) {
        // UI feedback on success
        setCopiedLinkId(linkId);
        setTimeout(() => setCopiedLinkId(null), 2000);

        setOpenSnackbar(true);
        setSnackbarMsg("Link copied!");
        setSnackbarSeverity("success");
      } else {
        throw new Error("Copy command returned false");
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);

      setOpenSnackbar(true);
      setSnackbarMsg("Failed to copy link. Please copy manually.");
      setSnackbarSeverity("error");
    } finally {
      // Always clean up
      document.body.removeChild(textarea);
    }
  };

  // Delete a link
  const deleteLinkHandler = async (link_id) => {
    try {
      const deleteLinkURL = `${API_BASE_URL}/api/auth/links/${link_id}`;
      const deleteLinkRes = await axios.delete(deleteLinkURL);

      if (!deleteLinkRes.data?.success) {
        throw deleteLinkRes.data;
      }

      setOpenSnackbar(true);
      setSnackbarSeverity("success");
      setSnackbarMsg(deleteLinkRes.data?.message || "Link deleted");

      // ✅ Proper refresh trigger
      setRefreshPage((prev) => !prev);
    } catch (error) {
      console.error(error);
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
      setSnackbarMsg("Failed to delete link");
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#394A5C",
        minHeight: "100vh",
        p: isMobile ? 2 : 6,
      }}
    >
      {/* Back, Home & Excel Export Buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        {/* Left side: Back + Home */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {[
            {
              icon: (
                <ArrowBackIosNewRoundedIcon
                  sx={{ fontSize: 20, fill: "#42A996" }}
                />
              ),
              onClick: () => navigate(-1),
            },
            {
              icon: <HomeRoundedIcon sx={{ fontSize: 20, fill: "#42A996" }} />,
              onClick: () => navigate("/admin-dash"),
            },
          ].map((btn, i) => (
            <Box
              key={i}
              sx={{
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 3,
                bgcolor: "#FFF",
                cursor: "pointer",
                "&:hover": { bgcolor: "#e0e0e0" },
              }}
              onClick={btn.onClick}
            >
              {btn.icon}
            </Box>
          ))}
        </Box>

        {/* Right side: Excel Export */}
        <Box>
          <IconButton
            onClick={exportToExcel}
            sx={{
              bgcolor: "#FFF",
              borderRadius: 2,
              width: 36,
              height: 36,
              "&:hover": {
                bgcolor: "#1c4b42ff",
                "& svg": { fill: "#ffffff" },
              },
              "& svg": {
                transition: "0.2s",
                fill: "#42A996",
              },
            }}
            aria-label="Export Excel"
          >
            <ExcelExportIcon
              style={{
                width: 18,
                height: 18,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* Heading */}
      <Typography
        sx={{
          color: "#fff",
          fontSize: { md: 24, sm: 20, xs: 16 },
          mb: 2,
          fontWeight: 600,
        }}
      >
        Link Management
      </Typography>

      {/* Filters & Create Link Button */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        {/* Filters */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            flex: 1,
            flexWrap: "wrap",
          }}
        >
          <TextField
            placeholder="Search by Link Title"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              flex: { xs: "1 1 100%", sm: 1 },
              bgcolor: "#fff",
              borderRadius: 1,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#667A8A" }} />
                </InputAdornment>
              ),
            }}
          />

          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 140 },
              bgcolor: "#fff",
              borderRadius: 1,
            }}
          >
            <MenuItem value="all">Type (All / Public)</MenuItem>
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="private">Private</MenuItem>
          </Select>

          <Select
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 120 },
              bgcolor: "#fff",
              borderRadius: 1,
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="week">This Week</MenuItem>
            <MenuItem value="month">This Month</MenuItem>
          </Select>
        </Box>

        {/* Create Link Button */}
        <Box sx={{ mt: { xs: 1, sm: 0 } }}>
          <Button
            onClick={() => navigate("/uc_createfilter")}
            sx={{
              bgcolor: "#42A996",
              color: "#fff",
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              width: { xs: "100%", sm: "auto" },
              "&:hover": { bgcolor: "#3b927f" },
            }}
          >
            Create Link
          </Button>
        </Box>
      </Box>

      {/* Links Table */}
      <Box
        sx={{
          bgcolor: "#2A3A4B",
          borderRadius: 2,
          overflowX: "auto",
          "& table": { minWidth: 650, tableLayout: "fixed" },
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "rgba(30,47,64,0.95)" }}>
              {/* Title */}
              <TableCell
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: 13, sm: 15 },
                  width: "25%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <LinkIcon
                      sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Title
                  </Typography>
                </Box>
              </TableCell>

              {/* Type */}
              <TableCell sx={{ color: "#fff", fontWeight: 600, width: "10%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <AttachFileRoundedIcon
                      sx={{
                        fontSize: { xs: 18, sm: 20 },
                        fill: "#FFF",
                        transform: "rotate(45deg)",
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Type
                  </Typography>
                </Box>
              </TableCell>

              {/* Views */}
              <TableCell sx={{ color: "#fff", fontWeight: 600, width: "15%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <VisibilityRoundedIcon
                      sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Views
                  </Typography>
                </Box>
              </TableCell>

              {/* Cart Items */}
              <TableCell sx={{ color: "#fff", fontWeight: 600, width: "15%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <ShoppingCartRoundedIcon
                      sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Cart Items
                  </Typography>
                </Box>
              </TableCell>

              {/* Created On */}
              <TableCell sx={{ color: "#fff", fontWeight: 600, width: "20%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <EventAvailableRoundedIcon
                      sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Created On
                  </Typography>
                </Box>
              </TableCell>

              {/* URL */}
              <TableCell sx={{ color: "#fff", fontWeight: 600, width: "15%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <ContentCopyRoundedIcon
                      sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Copy URL
                  </Typography>
                </Box>
              </TableCell>

              {/* Action */}
              <TableCell sx={{ color: "#fff", fontWeight: 600, width: "10%" }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedLinks.map((link, index) => (
              <TableRow
                key={index}
                sx={{
                  bgcolor: index % 2 === 0 ? "#1E2F40" : "#2A3A4B",
                  cursor: "pointer",
                }}
                onClick={() =>
                  navigate(`/active-link-logs/${link.linkId}`, {
                    state: {
                      linkName: link.name,
                      linkType: link.type,
                      linkCreation: link.createdAt,
                      linkExpiry: link.expiryDate,
                    },
                  })
                }
              >
                {/* Title */}
                <TableCell sx={{ color: "#fff" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography noWrap>{link.name}</Typography>
                  </Box>
                </TableCell>

                {/* Type */}
                <TableCell
                  sx={{
                    color:
                      link.type === "public"
                        ? "#42A996"
                        : "rgba(255,255,255,0.8)",
                    textTransform: "capitalize",
                  }}
                >
                  {link.type}
                </TableCell>

                {/* Views */}
                <TableCell sx={{ color: "#fff" }}>{link.views}</TableCell>

                {/* Cart Items */}
                <TableCell sx={{ color: "#fff" }}>
                  {itemsLockedCount[link.linkId] ?? "--"}
                </TableCell>

                {/* Created On */}
                <TableCell sx={{ color: "#fff" }}>
                  {formatDate(link.createdAt)}
                </TableCell>

                {/* URL */}
                <TableCell>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(
                        link.name,
                        link.linkId,
                        link.type,
                        link.filterCondition,
                      );
                    }}
                    sx={{
                      bgcolor:
                        copiedLinkId === link.linkId ? "#2e7d32" : "#42A996",
                      color: "#fff",
                      p: 1,
                      borderRadius: 100,
                      textTransform: "none",
                      fontSize: 12,
                      minWidth: 36,
                      transition: "all 0.25s ease",
                      "&:hover": {
                        bgcolor:
                          copiedLinkId === link.linkId ? "#27642a" : "#3b927f",
                      },
                    }}
                  >
                    {copiedLinkId === link.linkId ? (
                      <DoneRoundedIcon />
                    ) : (
                      <ContentCopyRoundedIcon />
                    )}
                  </Button>
                </TableCell>

                {/* Edit / Delete Buttons */}
                <TableCell>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {/* Edit Button */}
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/uc_createfilter", {
                          state: {
                            link_id: link.linkId,
                            link_privacy: link.type,
                          },
                        });
                      }}
                      sx={{
                        bgcolor: "#4291a9ff",
                        color: "#fff",
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        textTransform: "none",
                        fontSize: 12,
                        minWidth: 100,
                        transition: "all 0.25s ease",
                        "&:hover": {
                          bgcolor: "#3b7e92ff",
                        },
                      }}
                      startIcon={<EditRoundedIcon />}
                    >
                      Edit
                    </Button>

                    {/* Delete Button */}
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDeleteDialogBox({
                          open: true,
                          linkID: link.linkId,
                          linkName: link.name,
                        });
                      }}
                      sx={{
                        bgcolor: "#a94242ff",
                        color: "#fff",
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        textTransform: "none",
                        fontSize: 12,
                        minWidth: 100,
                        transition: "all 0.25s ease",
                        "&:hover": {
                          bgcolor: "#962c2cff",
                        },
                      }}
                      startIcon={<DeleteForeverRoundedIcon />}
                    >
                      Delete
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Pagination */}
      {filteredLinks.length > ITEMS_PER_PAGE && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <Pagination
            count={Math.ceil(filteredLinks.length / ITEMS_PER_PAGE)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#fff",
              },
              "& .Mui-selected": {
                bgcolor: "#42A996 !important",
                color: "#fff",
              },
            }}
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialogBox.open}
        onClose={() =>
          setOpenDeleteDialogBox({ open: false, linkID: null, linkName: null })
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Confirm Delete
          <IconButton
            onClick={() =>
              setOpenDeleteDialogBox({
                open: false,
                linkID: null,
                linkName: null,
              })
            }
            size="small"
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography sx={{ fontSize: 14 }}>
            Are you sure you want to delete link-{" "}
            <strong>{openDeleteDialogBox.linkName}</strong>
            <br />
            <span style={{ color: "red" }}>This action is irreversible.</span>
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            onClick={() =>
              setOpenDeleteDialogBox({
                open: false,
                linkID: null,
                linkName: null,
              })
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={() => {
              deleteLinkHandler(openDeleteDialogBox.linkID);
              setOpenDeleteDialogBox({
                open: false,
                linkID: null,
                linkName: null,
              });
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShowActiveLinks;
